import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { PageTransitionWrapper } from '../../shared/ui/PageTransitionWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';
import { Login } from '../../features/auth/pages/Login';
// Lazy-loaded feature pages. Named exports are mapped to default for lazy.
const lazyNamed = <T extends Record<string, unknown>,>(loader: () => Promise<T>, name: keyof T) => lazy(() => loader().then(m => ({
  default: m[name] as ComponentType<unknown>
})));
// analytics
const Overview = lazyNamed(() => import('../../features/analytics'), 'Overview');
const CustomMetrics = lazyNamed(() => import('../../features/analytics'), 'CustomMetrics');
const Nadi = lazyNamed(() => import('../../features/analytics'), 'Nadi');
const Reports = lazyNamed(() => import('../../features/analytics'), 'Reports');
const CardMonitoringPrograms = lazyNamed(() => import('../../features/analytics'), 'CardMonitoringPrograms');
const VampDetail = lazyNamed(() => import('../../features/analytics'), 'VampDetail');
const PaymentAnalytics = lazyNamed(() => import('../../features/analytics'), 'PaymentAnalytics');
// notifications
const Notifications = lazyNamed(() => import('../../features/notifications'), 'Notifications');
// payments
const PaymentRequests = lazyNamed(() => import('../../features/payments'), 'PaymentRequests');
const BulkPayments = lazyNamed(() => import('../../features/payments'), 'BulkPayments');
const BulkPaymentDetail = lazyNamed(() => import('../../features/payments'), 'BulkPaymentDetail');
const VirtualTerminal = lazyNamed(() => import('../../features/payments'), 'VirtualTerminal');
const QRCodes = lazyNamed(() => import('../../features/payments'), 'QRCodes');
// settings
const Settings = lazyNamed(() => import('../../features/settings'), 'Settings');
const UpdatePayoutAccount = lazyNamed(() => import('../../features/settings'), 'UpdatePayoutAccount');
// balance
const PayoutSettings = lazyNamed(() => import('../../features/balance'), 'PayoutSettings');
const BalanceSummary = lazyNamed(() => import('../../features/balance'), 'BalanceSummary');
const BalanceActivity = lazyNamed(() => import('../../features/balance'), 'BalanceActivity');
const BalanceActivityDetail = lazyNamed(() => import('../../features/balance'), 'BalanceActivityDetail');
const Payouts = lazyNamed(() => import('../../features/balance'), 'Payouts');
const PayoutDetail = lazyNamed(() => import('../../features/balance'), 'PayoutDetail');
// terminals
const PhysicalTerminals = lazyNamed(() => import('../../features/terminals'), 'PhysicalTerminals');
const SoftPosTerminals = lazyNamed(() => import('../../features/terminals'), 'SoftPosTerminals');
const Connect = lazyNamed(() => import('../../features/terminals'), 'Connect');
// developers
const WebhookEndpoints = lazyNamed(() => import('../../features/developers'), 'WebhookEndpoints');
const WebhookLogs = lazyNamed(() => import('../../features/developers'), 'WebhookLogs');
// tutorials
const Tutorials = lazyNamed(() => import('../../features/tutorials'), 'Tutorials');
const TutorialDetail = lazyNamed(() => import('../../features/tutorials'), 'TutorialDetail');
// shops (orders, shops, campaigns, sales, inventory)
const Orders = lazyNamed(() => import('../../features/shops'), 'Orders');
const OrderDetail = lazyNamed(() => import('../../features/shops'), 'OrderDetail');
const Shops = lazyNamed(() => import('../../features/shops'), 'Shops');
const ShopDetails = lazyNamed(() => import('../../features/shops'), 'ShopDetails');
const Campaigns = lazyNamed(() => import('../../features/shops'), 'Campaigns');
const CampaignDetails = lazyNamed(() => import('../../features/shops'), 'CampaignDetails');
const CampaignForm = lazyNamed(() => import('../../features/shops'), 'CampaignForm');
const Inventory = lazyNamed(() => import('../../features/shops'), 'Inventory');
const Sales = lazyNamed(() => import('../../features/shops'), 'Sales');
const SaleDetail = lazyNamed(() => import('../../features/shops'), 'SaleDetail');
const ShopSettings = lazyNamed(() => import('../../features/shops'), 'ShopSettings');
const ProductDetail = lazyNamed(() => import('../../features/shops'), 'ProductDetail');
const ProductEdit = lazyNamed(() => import('../../features/shops'), 'ProductEdit');
// rooms
const PropertyInformation = lazyNamed(() => import('../../features/rooms'), 'PropertyInformation');
const Rooms = lazyNamed(() => import('../../features/rooms'), 'Rooms');
const AddRoom = lazyNamed(() => import('../../features/rooms'), 'AddRoom');
const EditRoom = lazyNamed(() => import('../../features/rooms'), 'EditRoom');
const RoomDetail = lazyNamed(() => import('../../features/rooms'), 'RoomDetail');
const Rates = lazyNamed(() => import('../../features/rooms'), 'Rates');
const Reservations = lazyNamed(() => import('../../features/rooms'), 'Reservations');
const ReservationDetail = lazyNamed(() => import('../../features/rooms'), 'ReservationDetail');
// invoices
const Invoices = lazyNamed(() => import('../../features/invoices'), 'Invoices');
const InvoiceDetail = lazyNamed(() => import('../../features/invoices'), 'InvoiceDetail');
const InvoiceForm = lazyNamed(() => import('../../features/invoices'), 'InvoiceForm');
// customers
const Customers = lazyNamed(() => import('../../features/customers'), 'Customers');
const CustomerDetail = lazyNamed(() => import('../../features/customers'), 'CustomerDetail');
const CustomerForm = lazyNamed(() => import('../../features/customers'), 'CustomerForm');
// suppliers
const Suppliers = lazyNamed(() => import('../../features/suppliers'), 'Suppliers');
const SupplierDetail = lazyNamed(() => import('../../features/suppliers'), 'SupplierDetail');
const SupplierForm = lazyNamed(() => import('../../features/suppliers'), 'SupplierForm');
const SupplierSelfService = lazyNamed(() => import('../../features/suppliers'), 'SupplierSelfService');
const BulkSupplierUpload = lazyNamed(() => import('../../features/suppliers'), 'BulkSupplierUpload');
// bills
const Bills = lazyNamed(() => import('../../features/bills'), 'Bills');
const BillDetail = lazyNamed(() => import('../../features/bills'), 'BillDetail');
const BillForm = lazyNamed(() => import('../../features/bills'), 'BillForm');
const BillApprovalWizard = lazyNamed(() => import('../../features/bills'), 'BillApprovalWizard');
const BillFinancingWizard = lazyNamed(() => import('../../features/bills'), 'BillFinancingWizard');
const BulkBillUpload = lazyNamed(() => import('../../features/bills'), 'BulkBillUpload');
// disputes
const Disputes = lazyNamed(() => import('../../features/disputes'), 'Disputes');
const DisputeDetail = lazyNamed(() => import('../../features/disputes'), 'DisputeDetail');
const ChallengeDispute = lazyNamed(() => import('../../features/disputes'), 'ChallengeDispute');
// refunds
const Refunds = lazyNamed(() => import('../../features/refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('../../features/refunds'), 'RefundDetail');
// system
const ActivityLogs = lazyNamed(() => import('../../features/system'), 'ActivityLogs');
const Requests = lazyNamed(() => import('../../features/system'), 'Requests');
const RequestDetail = lazyNamed(() => import('../../features/system'), 'RequestDetail');
export function AppRouter() {
  return <BrowserRouter data-id="element-233">
      <Suspense fallback={<LoadingScreen data-id="element-235" />} data-id="element-234">
        <Routes data-id="element-236">
          <Route path="/" element={<Login data-id="element-238" />} data-id="element-237" />
          <Route path="/supplier-onboarding/:id" element={<SupplierSelfService data-id="element-240" />} data-id="element-239" />

          <Route path="/overview" element={<ProtectedRoute data-id="element-242">
                <AppLayout data-id="element-243">
                  <PageTransitionWrapper data-id="element-244">
                    <Overview data-id="element-245" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-241" />
          <Route path="/notifications" element={<ProtectedRoute data-id="element-247">
                <AppLayout data-id="element-248">
                  <PageTransitionWrapper data-id="element-249">
                    <Notifications data-id="element-250" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-246" />
          <Route path="/payment-requests" element={<ProtectedRoute data-id="element-252">
                <AppLayout data-id="element-253">
                  <PageTransitionWrapper data-id="element-254">
                    <PaymentRequests data-id="element-255" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-251" />
          <Route path="/qr-codes" element={<ProtectedRoute data-id="element-257">
                <AppLayout data-id="element-258">
                  <PageTransitionWrapper data-id="element-259">
                    <QRCodes data-id="element-260" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-256" />
          <Route path="/bulk-payments" element={<ProtectedRoute data-id="element-262">
                <AppLayout data-id="element-263">
                  <PageTransitionWrapper data-id="element-264">
                    <BulkPayments data-id="element-265" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-261" />
          <Route path="/bulk-payment-detail/:id" element={<ProtectedRoute data-id="element-267">
                <AppLayout data-id="element-268">
                  <PageTransitionWrapper data-id="element-269">
                    <BulkPaymentDetail data-id="element-270" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-266" />
          <Route path="/settings" element={<ProtectedRoute data-id="element-272">
                <AppLayout data-id="element-273">
                  <PageTransitionWrapper data-id="element-274">
                    <Settings data-id="element-275" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-271" />
          <Route path="/settings/payouts" element={<ProtectedRoute data-id="element-277">
                <AppLayout data-id="element-278">
                  <PageTransitionWrapper data-id="element-279">
                    <PayoutSettings data-id="element-280" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-276" />
          <Route path="/settings/payout-account/edit" element={<ProtectedRoute data-id="element-282">
                <AppLayout data-id="element-283">
                  <PageTransitionWrapper data-id="element-284">
                    <UpdatePayoutAccount data-id="element-285" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-281" />
          <Route path="/virtual-terminal" element={<ProtectedRoute data-id="element-287">
                <AppLayout data-id="element-288">
                  <PageTransitionWrapper data-id="element-289">
                    <VirtualTerminal data-id="element-290" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-286" />
          <Route path="/physical-terminals" element={<ProtectedRoute data-id="element-292">
                <AppLayout data-id="element-293">
                  <PageTransitionWrapper data-id="element-294">
                    <PhysicalTerminals data-id="element-295" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-291" />
          <Route path="/softpos-terminals" element={<ProtectedRoute data-id="element-297">
                <AppLayout data-id="element-298">
                  <PageTransitionWrapper data-id="element-299">
                    <SoftPosTerminals data-id="element-300" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-296" />
          <Route path="/connect" element={<ProtectedRoute data-id="element-302">
                <AppLayout data-id="element-303">
                  <PageTransitionWrapper data-id="element-304">
                    <Connect data-id="element-305" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-301" />
          <Route path="/webhooks/endpoints" element={<ProtectedRoute data-id="element-307">
                <AppLayout data-id="element-308">
                  <PageTransitionWrapper data-id="element-309">
                    <WebhookEndpoints data-id="element-310" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-306" />
          <Route path="/webhooks/logs" element={<ProtectedRoute data-id="element-312">
                <AppLayout data-id="element-313">
                  <PageTransitionWrapper data-id="element-314">
                    <WebhookLogs data-id="element-315" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-311" />
          <Route path="/tutorials" element={<ProtectedRoute data-id="element-317">
                <AppLayout data-id="element-318">
                  <PageTransitionWrapper data-id="element-319">
                    <Tutorials data-id="element-320" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-316" />
          <Route path="/tutorial/:id" element={<ProtectedRoute data-id="element-322">
                <AppLayout data-id="element-323">
                  <PageTransitionWrapper data-id="element-324">
                    <TutorialDetail data-id="element-325" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-321" />
          <Route path="/orders" element={<ProtectedRoute data-id="element-327">
                <AppLayout data-id="element-328">
                  <PageTransitionWrapper data-id="element-329">
                    <Orders data-id="element-330" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-326" />
          <Route path="/order-detail/:id" element={<ProtectedRoute data-id="element-332">
                <AppLayout data-id="element-333">
                  <PageTransitionWrapper data-id="element-334">
                    <OrderDetail data-id="element-335" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-331" />
          <Route path="/shops" element={<ProtectedRoute data-id="element-337">
                <AppLayout data-id="element-338">
                  <PageTransitionWrapper data-id="element-339">
                    <Shops data-id="element-340" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-336" />
          <Route path="/shop-details" element={<ProtectedRoute data-id="element-342">
                <AppLayout data-id="element-343">
                  <PageTransitionWrapper data-id="element-344">
                    <ShopDetails data-id="element-345" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-341" />
          <Route path="/shop-settings/:shopId" element={<ProtectedRoute data-id="element-347">
                <AppLayout data-id="element-348">
                  <PageTransitionWrapper data-id="element-349">
                    <ShopSettings data-id="element-350" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-346" />
          <Route path="/shop-settings" element={<ProtectedRoute data-id="element-352">
                <AppLayout data-id="element-353">
                  <PageTransitionWrapper data-id="element-354">
                    <ShopSettings data-id="element-355" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-351" />
          <Route path="/product-detail/:productId" element={<ProtectedRoute data-id="element-357">
                <AppLayout data-id="element-358">
                  <PageTransitionWrapper data-id="element-359">
                    <ProductDetail data-id="element-360" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-356" />
          <Route path="/product-edit/:productId" element={<ProtectedRoute data-id="element-362">
                <AppLayout data-id="element-363">
                  <PageTransitionWrapper data-id="element-364">
                    <ProductEdit data-id="element-365" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-361" />
          <Route path="/campaigns" element={<ProtectedRoute data-id="element-367">
                <AppLayout data-id="element-368">
                  <PageTransitionWrapper data-id="element-369">
                    <Campaigns data-id="element-370" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-366" />
          <Route path="/campaign-details" element={<ProtectedRoute data-id="element-372">
                <AppLayout data-id="element-373">
                  <PageTransitionWrapper data-id="element-374">
                    <CampaignDetails data-id="element-375" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-371" />
          <Route path="/campaign-form" element={<ProtectedRoute data-id="element-377">
                <AppLayout data-id="element-378">
                  <PageTransitionWrapper data-id="element-379">
                    <CampaignForm data-id="element-380" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-376" />
          <Route path="/property-information" element={<ProtectedRoute data-id="element-382">
                <AppLayout data-id="element-383">
                  <PageTransitionWrapper data-id="element-384">
                    <PropertyInformation data-id="element-385" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-381" />
          <Route path="/rooms" element={<ProtectedRoute data-id="element-387">
                <AppLayout data-id="element-388">
                  <PageTransitionWrapper data-id="element-389">
                    <Rooms data-id="element-390" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-386" />
          <Route path="/add-room" element={<ProtectedRoute data-id="element-392">
                <AppLayout data-id="element-393">
                  <PageTransitionWrapper data-id="element-394">
                    <AddRoom data-id="element-395" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-391" />
          <Route path="/edit-room" element={<ProtectedRoute data-id="element-397">
                <AppLayout data-id="element-398">
                  <PageTransitionWrapper data-id="element-399">
                    <EditRoom data-id="element-400" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-396" />
          <Route path="/room-detail" element={<ProtectedRoute data-id="element-402">
                <AppLayout data-id="element-403">
                  <PageTransitionWrapper data-id="element-404">
                    <RoomDetail data-id="element-405" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-401" />
          <Route path="/inventory" element={<ProtectedRoute data-id="element-407">
                <AppLayout data-id="element-408">
                  <PageTransitionWrapper data-id="element-409">
                    <Inventory data-id="element-410" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-406" />
          <Route path="/rates" element={<ProtectedRoute data-id="element-412">
                <AppLayout data-id="element-413">
                  <PageTransitionWrapper data-id="element-414">
                    <Rates data-id="element-415" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-411" />
          <Route path="/reservations" element={<ProtectedRoute data-id="element-417">
                <AppLayout data-id="element-418">
                  <PageTransitionWrapper data-id="element-419">
                    <Reservations data-id="element-420" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-416" />
          <Route path="/reservation-detail" element={<ProtectedRoute data-id="element-422">
                <AppLayout data-id="element-423">
                  <PageTransitionWrapper data-id="element-424">
                    <ReservationDetail data-id="element-425" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-421" />
          <Route path="/invoices" element={<ProtectedRoute data-id="element-427">
                <AppLayout data-id="element-428">
                  <PageTransitionWrapper data-id="element-429">
                    <Invoices data-id="element-430" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-426" />
          <Route path="/invoice-detail/:id" element={<ProtectedRoute data-id="element-432">
                <AppLayout data-id="element-433">
                  <PageTransitionWrapper data-id="element-434">
                    <InvoiceDetail data-id="element-435" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-431" />
          <Route path="/invoice-form" element={<ProtectedRoute data-id="element-437">
                <AppLayout data-id="element-438">
                  <PageTransitionWrapper data-id="element-439">
                    <InvoiceForm data-id="element-440" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-436" />
          <Route path="/customers" element={<ProtectedRoute data-id="element-442">
                <AppLayout data-id="element-443">
                  <PageTransitionWrapper data-id="element-444">
                    <Customers data-id="element-445" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-441" />
          <Route path="/customer-detail/:id" element={<ProtectedRoute data-id="element-447">
                <AppLayout data-id="element-448">
                  <PageTransitionWrapper data-id="element-449">
                    <CustomerDetail data-id="element-450" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-446" />
          <Route path="/customer-form" element={<ProtectedRoute data-id="element-452">
                <AppLayout data-id="element-453">
                  <PageTransitionWrapper data-id="element-454">
                    <CustomerForm data-id="element-455" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-451" />
          <Route path="/sales" element={<ProtectedRoute data-id="element-457">
                <AppLayout data-id="element-458">
                  <PageTransitionWrapper data-id="element-459">
                    <Sales data-id="element-460" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-456" />
          <Route path="/sale-detail/:id" element={<ProtectedRoute data-id="element-462">
                <AppLayout data-id="element-463">
                  <PageTransitionWrapper data-id="element-464">
                    <SaleDetail data-id="element-465" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-461" />
          <Route path="/balance" element={<ProtectedRoute data-id="element-467">
                <AppLayout data-id="element-468">
                  <PageTransitionWrapper data-id="element-469">
                    <BalanceSummary data-id="element-470" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-466" />
          <Route path="/balance-activity" element={<ProtectedRoute data-id="element-472">
                <AppLayout data-id="element-473">
                  <PageTransitionWrapper data-id="element-474">
                    <BalanceActivity data-id="element-475" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-471" />
          <Route path="/balance-activity/:id" element={<ProtectedRoute data-id="element-477">
                <AppLayout data-id="element-478">
                  <PageTransitionWrapper data-id="element-479">
                    <BalanceActivityDetail data-id="element-480" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-476" />
          <Route path="/payouts" element={<ProtectedRoute data-id="element-482">
                <AppLayout data-id="element-483">
                  <PageTransitionWrapper data-id="element-484">
                    <Payouts data-id="element-485" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-481" />
          <Route path="/payouts/:id" element={<ProtectedRoute data-id="element-487">
                <AppLayout data-id="element-488">
                  <PageTransitionWrapper data-id="element-489">
                    <PayoutDetail data-id="element-490" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-486" />
          <Route path="/suppliers" element={<ProtectedRoute data-id="element-492">
                <AppLayout data-id="element-493">
                  <PageTransitionWrapper data-id="element-494">
                    <Suppliers data-id="element-495" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-491" />
          <Route path="/supplier-detail/:id" element={<ProtectedRoute data-id="element-497">
                <AppLayout data-id="element-498">
                  <PageTransitionWrapper data-id="element-499">
                    <SupplierDetail data-id="element-500" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-496" />
          <Route path="/supplier-form" element={<ProtectedRoute data-id="element-502">
                <AppLayout data-id="element-503">
                  <PageTransitionWrapper data-id="element-504">
                    <SupplierForm data-id="element-505" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-501" />
          <Route path="/supplier-bulk-upload" element={<ProtectedRoute data-id="element-507">
                <AppLayout data-id="element-508">
                  <PageTransitionWrapper data-id="element-509">
                    <BulkSupplierUpload data-id="element-510" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-506" />
          <Route path="/bill-bulk-upload" element={<ProtectedRoute data-id="element-512">
                <AppLayout data-id="element-513">
                  <PageTransitionWrapper data-id="element-514">
                    <BulkBillUpload data-id="element-515" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-511" />
          <Route path="/bills" element={<ProtectedRoute data-id="element-517">
                <AppLayout data-id="element-518">
                  <PageTransitionWrapper data-id="element-519">
                    <Bills data-id="element-520" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-516" />
          <Route path="/bill-detail/:id" element={<ProtectedRoute data-id="element-522">
                <AppLayout data-id="element-523">
                  <PageTransitionWrapper data-id="element-524">
                    <BillDetail data-id="element-525" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-521" />
          <Route path="/bill-form" element={<ProtectedRoute data-id="element-527">
                <AppLayout data-id="element-528">
                  <PageTransitionWrapper data-id="element-529">
                    <BillForm data-id="element-530" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-526" />
          <Route path="/bill-approval/:id" element={<ProtectedRoute data-id="element-532">
                <AppLayout data-id="element-533">
                  <PageTransitionWrapper data-id="element-534">
                    <BillApprovalWizard data-id="element-535" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-531" />
          <Route path="/bill-financing/:id" element={<ProtectedRoute data-id="element-537">
                <AppLayout data-id="element-538">
                  <PageTransitionWrapper data-id="element-539">
                    <BillFinancingWizard data-id="element-540" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-536" />
          <Route path="/disputes" element={<ProtectedRoute data-id="element-542">
                <AppLayout data-id="element-543">
                  <PageTransitionWrapper data-id="element-544">
                    <Disputes data-id="element-545" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-541" />
          <Route path="/dispute-detail/:id" element={<ProtectedRoute data-id="element-547">
                <AppLayout data-id="element-548">
                  <PageTransitionWrapper data-id="element-549">
                    <DisputeDetail data-id="element-550" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-546" />
          <Route path="/disputes/:id" element={<ProtectedRoute data-id="element-552">
                <AppLayout data-id="element-553">
                  <PageTransitionWrapper data-id="element-554">
                    <DisputeDetail data-id="element-555" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-551" />
          <Route path="/disputes/:id/challenge" element={<ProtectedRoute data-id="element-557">
                <ChallengeDispute data-id="element-558" />
              </ProtectedRoute>} data-id="element-556" />
          <Route path="/refunds" element={<ProtectedRoute data-id="element-560">
                <AppLayout data-id="element-561">
                  <PageTransitionWrapper data-id="element-562">
                    <Refunds data-id="element-563" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-559" />
          <Route path="/refund-detail/:id" element={<ProtectedRoute data-id="element-565">
                <AppLayout data-id="element-566">
                  <PageTransitionWrapper data-id="element-567">
                    <RefundDetail data-id="element-568" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-564" />
          <Route path="/custom-metrics" element={<ProtectedRoute data-id="element-570">
                <AppLayout data-id="element-571">
                  <PageTransitionWrapper data-id="element-572">
                    <CustomMetrics data-id="element-573" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-569" />
          <Route path="/nadi" element={<ProtectedRoute data-id="element-575">
                <AppLayout data-id="element-576">
                  <PageTransitionWrapper data-id="element-577">
                    <Nadi data-id="element-578" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-574" />
          <Route path="/reports" element={<ProtectedRoute data-id="element-580">
                <AppLayout data-id="element-581">
                  <PageTransitionWrapper data-id="element-582">
                    <Reports data-id="element-583" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-579" />
          <Route path="/card-monitoring" element={<ProtectedRoute data-id="element-585">
                <AppLayout data-id="element-586">
                  <PageTransitionWrapper data-id="element-587">
                    <CardMonitoringPrograms data-id="element-588" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-584" />
          <Route path="/card-monitoring/vamp" element={<ProtectedRoute data-id="element-590">
                <AppLayout data-id="element-591">
                  <PageTransitionWrapper data-id="element-592">
                    <VampDetail data-id="element-593" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-589" />
          <Route path="/payment-analytics" element={<ProtectedRoute data-id="element-595">
                <AppLayout data-id="element-596">
                  <PageTransitionWrapper data-id="element-597">
                    <PaymentAnalytics data-id="element-598" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-594" />
          <Route path="/activity-logs" element={<ProtectedRoute data-id="element-600">
                <AppLayout data-id="element-601">
                  <PageTransitionWrapper data-id="element-602">
                    <ActivityLogs data-id="element-603" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-599" />
          <Route path="/requests" element={<ProtectedRoute data-id="element-605">
                <AppLayout data-id="element-606">
                  <PageTransitionWrapper data-id="element-607">
                    <Requests data-id="element-608" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-604" />
          <Route path="/requests/:id" element={<ProtectedRoute data-id="element-610">
                <AppLayout data-id="element-611">
                  <PageTransitionWrapper data-id="element-612">
                    <RequestDetail data-id="element-613" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-609" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}